import {
  Alert,
  AppState,
  Linking,
  NativeEventSubscription,
  Platform,
  StyleSheet,
  Text,
  View,
} from "react-native";
import React, { useEffect, useRef } from "react";
import { AppTheme } from "../utils/theme";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { ROUTE_NAME } from "../utils/routes";
import HomeScreen from "./home";
import LoginScreen from "./login";
import { NavigationContainer } from "@react-navigation/native";
import PlayWithComputer from "./playwithcomputer";
import SelectPlayers from "./selectplayers";
import PlayWithFriends from "./playwithfriends";
import { useAppSelector } from "../redux/storeHook"; // Added dispatch import
import Sound from "react-native-sound";
import OnlinePlay from "./onlineplay";
import PlayerRoom from "./onlineplay/playerroom";
import { userAction } from "../redux/reducers/user";
import WaitingRoom from "./onlineplay/waitingroom";
import DeviceInfo from "react-native-device-info";
import HomeTabletScreen from "./home/homeTablet";
import SelectPlayersTablet from "./selectplayers/selectPlayerTablet";
import LoginScreenTablet from "./login/loginTablet";
import PlayWithFriendsTablet from "./playwithfriends/playFriendsTablet";
import PlayWithComputerTablet from "./playwithcomputer/playComputerTablet";
import PlayerRoomTablet from "./onlineplay/playerroom/playRoomTablet";
import WaitingRoomTablet from "./onlineplay/waitingroom/waitingRoomTablet";
import OnlinePlayTablet from "./onlineplay/onlinePlayTablet";
import { navRef } from "../utils/contants";
import { checkForUpdate } from "../utils/appUpdatesCheck";
import EditProfile from "./editProfile";
import EditProfileTablet from "./editProfile/editTablet";
import {
  activateKeepAwake,
  deactivateKeepAwake,
} from "@sayem314/react-native-keep-awake";
import InPurchaseScreen from "./inAppPurchase";
import LuckySpinScreen from "./spinScreen";
import RandomOnlinePlay from "./randomonlineplay";
import RandomPlayerRoom from "./randomonlineplay/randomplayerroom";
import RandomWaitingRoom from "./randomonlineplay/randomwaitingroom";
import TabletRandomPlayerRoom from "./randomonlineplay/randomplayerroom/tabletrandomplayerRoom";
import TabletRandomWaitingRoom from "./randomonlineplay/randomwaitingroom/tabletrandomwaitingroom";
import TabletRandomOnlinePlay from "./randomonlineplay/tabletrandomonlineplay";
import LuckySpinTablet from "./spinScreen/spinScreenTablet";
import InPurchaseScreenTablet from "./inAppPurchase/inAppPurchaseTablet";
import { useAppOpenAd } from "../utils/useOpenAppAd";

const Stack = createNativeStackNavigator<any>();
const StackNavigator = Stack.Navigator as any;

const RootRoute = () => {
  const { isMusicOn, isPlayMusicOn, isSoundOn, touchSound, playGame } =
    useAppSelector((state) => state.user);
  const { userData } = useAppSelector((s) => s.user);
  // Refs for sound instances
  const bgSoundRef = useRef<Sound | null>(null);
  const touchSoundRef = useRef<Sound | null>(null);
  const isTablet = DeviceInfo.isTablet();
  // const { showAdIfAvailable, hasShownOnColdStart } = useAppOpenAd();
  useAppOpenAd();

  useEffect(() => {
    checkForUpdate();
    activateKeepAwake();
    return () => {
      deactivateKeepAwake();
    };
  }, []);

  // useEffect(() => {
  //   // Wait for NavigationContainer to be ready + some UI to render
  //   const timer = setTimeout(async () => {
  //     if (!hasShownOnColdStart.current) {
  //       // You'll need to expose this or use a global flag
  //       console.log("Attempting cold start App Open Ad...");
  //       const shown = await showAdIfAvailable();
  //       if (!shown) {
  //         // Retry once after more delay if first attempt failed
  //         setTimeout(() => showAdIfAvailable(), 1500);
  //       }
  //     }
  //   }, 1200); // Adjust between 800ms - 1500ms

  //   return () => clearTimeout(timer);
  // }, [showAdIfAvailable]);

  // Preload sounds once on mount (unchanged, but added category for better playback)
  useEffect(() => {
    console.log("Preloading sounds");
    try {
      // Set audio category for better mixing (iOS/Android)
      Sound.setCategory("Playback");

      bgSoundRef.current = new Sound(
        "startgame.mp3",
        Sound.MAIN_BUNDLE,
        (error) => {
          if (error) {
            console.log("Failed to load bg sound", error);
            return;
          }
          bgSoundRef.current?.setNumberOfLoops(-1); // Infinite loop
          bgSoundRef.current?.setVolume(0.5); // Softer for mixing
          // Force initial play if defaults are true (fixes fresh install)
          if (isMusicOn && isPlayMusicOn) {
            bgSoundRef.current?.play((success) => {
              if (!success) console.log("Initial bg playback failed");
            });
          }
        },
      );

      touchSoundRef.current = new Sound(
        "touch.mp3",
        Sound.MAIN_BUNDLE,
        (error) => {
          if (error) {
            console.log("Failed to load touch sound", error);
            return;
          }
          touchSoundRef.current?.setVolume(0.8); // Louder for touch
        },
      );
    } catch (error) {
      console.log("Sound preload error", error);
    }

    // Cleanup on unmount (consolidated)
    return () => {
      bgSoundRef.current?.stop(() => bgSoundRef.current?.release());
      bgSoundRef.current = null;
      touchSoundRef.current?.stop(() => touchSoundRef.current?.release());
      touchSoundRef.current = null;
    };
  }, []); // No deps change

  // Play/stop background based on state (unchanged, this handles async updates)
  useEffect(() => {
    if (isMusicOn && isPlayMusicOn && !playGame) {
      bgSoundRef.current?.play((success) => {
        if (!success) {
          console.log("Background playback failed");
        }
      });
    } else {
      bgSoundRef.current?.stop();
    }
  }, [isMusicOn, isPlayMusicOn, playGame]);

  // App state for background music pause/resume (fixed: removed redundant play, rely on useEffect)
  useEffect(() => {
    const subscription = AppState.addEventListener("change", (nextState) => {
      if (
        nextState === "background" &&
        isPlayMusicOn &&
        isMusicOn &&
        !playGame
      ) {
        userAction.setMusicPreference(false); // Dispatch instead of direct action
        bgSoundRef.current?.stop();
      } else if (
        nextState === "active" &&
        isPlayMusicOn &&
        !isMusicOn &&
        !playGame
      ) {
        userAction.setMusicPreference(true); // Dispatch async, useEffect will play after update
        // Removed buggy immediate play check
      }
    });
    return () => {
      subscription.remove();
    };
  }, [isMusicOn, isPlayMusicOn, playGame]);

  // App state for touch sound stop on background (minor: use dispatch if needed, but ok)
  useEffect(() => {
    const subscription = AppState.addEventListener("change", (nextState) => {
      if (nextState === "background" && isSoundOn) {
        touchSoundRef.current?.stop();
      }
    });
    return () => {
      subscription.remove();
    };
  }, [isSoundOn]);

  // Touch sound play (major fixes: release in callback, add retry, no timeout)
  useEffect(() => {
    if (touchSound && touchSoundRef.current && isSoundOn) {
      let playCount = 0;
      const maxRetries = 2; // Retry up to 2 times on failure

      const attemptPlay = () => {
        playCount++;
        touchSoundRef.current?.play((success) => {
          console.log("Touch play success:", success, "attempt:", playCount);
          if (success) {
            // Success: reset state and release AFTER play completes
            userAction.setTouchSound(false);
            // Get duration to wait properly before release (async)
            //@ts-ignore
            touchSoundRef.current?.getDuration((duration) => {
              if (duration && duration > 0) {
                setTimeout(() => {
                  if (!touchSoundRef.current?.isPlaying()) {
                    touchSoundRef.current?.release();
                    // Re-preload for next use
                    touchSoundRef.current = new Sound(
                      "touch.mp3",
                      Sound.MAIN_BUNDLE,
                      (error) => {
                        if (error) {
                          console.log("Re-preload touch error", error);
                        } else {
                          touchSoundRef.current?.setVolume(0.8);
                        }
                      },
                    );
                  }
                }, duration * 1000 + 50); // Wait full duration + buffer
              } else {
                // Fallback release if no duration
                touchSoundRef.current?.release();
                // Re-preload immediately
                touchSoundRef.current = new Sound(
                  "touch.mp3",
                  Sound.MAIN_BUNDLE,
                  (error) => {
                    if (error) console.log("Fallback re-preload error", error);
                  },
                );
              }
            });
          } else if (playCount <= maxRetries) {
            console.log("Touch playback failed, retrying...");
            setTimeout(attemptPlay, 50); // Short delay before retry
          } else {
            console.log("Touch playback failed after retries");
            userAction.setTouchSound(false); // Reset on final failure
          }
        });
      };

      try {
        attemptPlay();
      } catch (error) {
        console.log("Touch sound error", error);
        userAction.setTouchSound(false);
      }
    }
  }, [touchSound, isSoundOn]); // Deps unchanged

  useEffect(() => {
    // Cold start (app was closed)
    Linking.getInitialURL().then((url) => {
      if (url) {
        handleUrl(url);
      }
    });

    // Warm start (app already open)
    const sub = Linking.addEventListener("url", ({ url }) => {
      handleUrl(url);
    });

    return () => {
      sub.remove();
    };
  }, []);

  const handleUrl = (url: string) => {
    if (!url) return;
    const hasJoinRoom =
      url.includes("joinroom") ||
      url.includes("roomId=") ||
      url.includes("share.ludovoice.com");
    if (!hasJoinRoom) return;

    const id = url?.split("=")[1];
    if (!id) return;

    console.log("🔗 Received Deep Link URL:", url, id);
    if (!userData?.name) {
      Alert.alert("Login", "To play online you need to first login", [
        {
          text: "Login",
          onPress: () => {
            if (navRef?.isReady()) {
              // @ts-ignore
              navRef?.navigate(ROUTE_NAME.LoginScreen, { roomId: id });
            }
          },
        },
        {
          text: "Cancel",
          style: "cancel",
        },
      ]);
      return;
    }
    if (id) {
      if (navRef?.isReady()) {
        // @ts-ignore
        navRef?.navigate(ROUTE_NAME.PlayerRoom, { roomId: id });
      }
    }
  };

  const win = typeof window !== "undefined" ? (window as any) : null;

  const isFileProtocol =
    Platform.OS === "web" &&
    win &&
    win.location?.protocol === "file:";

  const linkingConfig = isFileProtocol
    ? undefined
    : {
      prefixes: [
        "https://share.ludovoice.com/",
        "ludovoice://",
        ...(Platform.OS === "web" &&
          win &&
          win.location?.origin &&
          win.location.origin !== "null"
          ? [win.location.origin, win.location.origin + "/"]
          : []),
      ],
      config: {
        screens: {
          [ROUTE_NAME.HomeScreen]: "",
          [ROUTE_NAME.LoginScreen]: "login",
          [ROUTE_NAME.PlayWithComputer]: "play-with-computer",
          [ROUTE_NAME.SelectPlayer]: "select-player",
          [ROUTE_NAME.PlayWithFriend]: "play-with-friend",
          [ROUTE_NAME.PlayerRoom]: {
            path: "joinroom",
            parse: {
              roomId: (value: any) => value,
            },
          },
          [ROUTE_NAME.WaitingRoom]: "waiting-room",
          [ROUTE_NAME.PlayOnline]: "play-online",
          [ROUTE_NAME.EditProfile]: "edit-profile",
          [ROUTE_NAME.InPurchaseScreen]: "store",
          [ROUTE_NAME.LuckySpinScreen]: "lucky-spin",
          [ROUTE_NAME.RandomOnlinePlay]: "random-online",
          [ROUTE_NAME.RandomPlayerRoom]: "random-room",
          [ROUTE_NAME.RandomWaitingRoom]: "random-waiting",
        },
      },
    };

  return (
    <NavigationContainer
      ref={navRef}
      linking={linkingConfig}
    >
      <StackNavigator
        screenOptions={{
          headerShown: false,
          gestureEnabled: false,
          contentStyle: {
            backgroundColor: AppTheme.colors.linearGradient_3,
          },
        }}
      >
        <Stack.Screen
          name={ROUTE_NAME.HomeScreen}
          component={isTablet ? HomeTabletScreen : HomeScreen}
        />
        <Stack.Screen
          name={ROUTE_NAME.LoginScreen}
          component={isTablet ? LoginScreenTablet : LoginScreen}
        />
        <Stack.Screen
          name={ROUTE_NAME.PlayWithComputer}
          component={isTablet ? PlayWithComputerTablet : PlayWithComputer}
        />
        <Stack.Screen
          name={ROUTE_NAME.SelectPlayer}
          component={isTablet ? SelectPlayersTablet : SelectPlayers}
        />
        <Stack.Screen
          name={ROUTE_NAME.PlayWithFriend}
          component={isTablet ? PlayWithFriendsTablet : PlayWithFriends}
        />
        <Stack.Screen
          name={ROUTE_NAME.PlayerRoom}
          component={isTablet ? PlayerRoomTablet : PlayerRoom}
        />
        <Stack.Screen
          name={"PlayerRoom"}
          component={isTablet ? PlayerRoomTablet : PlayerRoom}
        />
        <Stack.Screen
          name={ROUTE_NAME.WaitingRoom}
          component={isTablet ? WaitingRoomTablet : WaitingRoom}
        />
        <Stack.Screen
          name={ROUTE_NAME.PlayOnline}
          component={isTablet ? OnlinePlayTablet : OnlinePlay}
        />
        <Stack.Screen
          name={ROUTE_NAME.EditProfile}
          component={isTablet ? EditProfileTablet : EditProfile}
        />
        <Stack.Screen
          name={ROUTE_NAME.InPurchaseScreen}
          component={isTablet ? InPurchaseScreenTablet : InPurchaseScreen}
        />
        <Stack.Screen
          name={ROUTE_NAME.LuckySpinScreen}
          component={isTablet ? LuckySpinTablet : LuckySpinScreen}
        />
        {/* random player room */}
        <Stack.Screen
          name={ROUTE_NAME.RandomOnlinePlay}
          component={isTablet ? TabletRandomOnlinePlay : RandomOnlinePlay}
        />
        <Stack.Screen
          name={ROUTE_NAME.RandomPlayerRoom}
          component={isTablet ? TabletRandomPlayerRoom : RandomPlayerRoom}
        />
        <Stack.Screen
          name={ROUTE_NAME.RandomWaitingRoom}
          component={isTablet ? TabletRandomWaitingRoom : RandomWaitingRoom}
        />
      </StackNavigator>
    </NavigationContainer>
  );
};

export default RootRoute;

const styles = StyleSheet.create({
  mainView: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  text: {
    fontSize: 55,
    fontFamily: AppTheme.fonts.BUBBLE_BOBBLE,
  },
});
